import { Avatar, Input, List, Typography, Badge } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import ChatDetails from "./ChatDetails";
import { useSelector } from "react-redux";
import axios from "axios";
import { BASE_API_CONFIG } from "../../util/Api";
import { CHAT_BUSINESS_TYPE_ID } from "../../util/Constant";
import { isValidString } from "../../util/ApiHelper";

const ChatList = () => {
  const { isMobile } = useGetBreakpoints();
  const [selectedChat, setSelectedChat] = useState(null);
  const [sendMessage, setSendMessage] = useState("");
  const [chatList, setChatList] = useState([]);
  const authUser = useSelector(({ auth }) => auth.userData);
  const userId = authUser.id;
  const token = authUser.token;
  const [loading, setLoading] = useState(false);
  const [showChatDetails, setShowChatDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChatRatings, setSelectedChatRatings] = useState(null);
  const getConversationKey = useCallback((chat) => {
    if (!chat) return null;

    const parts = [];

    if (chat.quotationId) parts.push(`quotation-${chat.quotationId}`);
    if (chat.enquiryId) parts.push(`enquiry-${chat.enquiryId}`);
    if (chat.promotionId) parts.push(`promotion-${chat.promotionId}`);

    if (parts.length > 0) {
      return parts.join("__");
    }

    return `direct-${Math.min(chat.senderId, chat.receiverId)}-${Math.max(
      chat.senderId,
      chat.receiverId
    )}`;
  }, []);

  const getBusinessType = (businessTypeId) => {
    return CHAT_BUSINESS_TYPE_ID[businessTypeId] || "Unknown";
  };

  const fetchChatList = async () => {
  try {
    const response = await axios.get(
      `${BASE_API_CONFIG.baseURL}/messages/chat-list/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setLoading(false);

    const chatMap = new Map();

    response.data.data.forEach((chat) => {
      const messages = [...(chat.messages || [])];
      messages.sort(
        (a, b) =>
          new Date(b.timestamp || b.createdAt) -
          new Date(a.timestamp || a.createdAt)
      );
      const latestMessage = messages.length > 0 ? messages[0] : null;
      const unreadCount = messages.filter(
        (msg) => !msg.isread && msg.senderId !== userId
      ).length;

      // Keep each enquiry / quotation / promotion thread separate.
      const uniqueKey = getConversationKey(chat);

      const formattedChat = {
        ...chat,
        messages,
        businessType: getBusinessType(
          chat.receiverId === userId
            ? chat.sender.businessType
            : chat.receiver.businessType
        ),
        lastMessageTimestamp: latestMessage
          ? new Date(latestMessage.timestamp || latestMessage.createdAt).getTime()
          : 0,
        lastMessageText: latestMessage ? latestMessage.text : "",
        latestMessageTimestamp: latestMessage
          ? latestMessage.timestamp || latestMessage.createdAt
          : null,
        unreadCount,
      };

      // Always keep the most recent chat if duplicates exist
      if (
        !chatMap.has(uniqueKey) ||
        chatMap.get(uniqueKey).lastMessageTimestamp < formattedChat.lastMessageTimestamp
      ) {
        chatMap.set(uniqueKey, formattedChat);
      }
    });

    const updatedChatList = Array.from(chatMap.values()).sort(
      (a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp
    );

    setChatList(updatedChatList);
    setSelectedChat((prevSelectedChat) => {
      if (!prevSelectedChat) {
        return prevSelectedChat;
      }

      const refreshedSelectedChat = updatedChatList.find(
        (chat) => getConversationKey(chat) === getConversationKey(prevSelectedChat)
      );

      return refreshedSelectedChat || prevSelectedChat;
    });
  } catch (error) {
    setLoading(false);
    console.error("Error fetching chat list:", error);
  }
};

  useEffect(() => {
    setLoading(true);
    fetchChatList();
  }, [userId, token]);

  const isYesterday = (timestamp) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const messageDate = new Date(timestamp);

    return (
      messageDate.getDate() === yesterday.getDate() &&
      messageDate.getMonth() === yesterday.getMonth() &&
      messageDate.getFullYear() === yesterday.getFullYear()
    );
  };

  const formatIndianTime = (msgTimestamp) => {
    return new Date(msgTimestamp).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";

    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (isYesterday(timestamp)) {
      return "Yesterday";
    } else if (messageDate < yesterday) {
      const day = String(messageDate.getDate()).padStart(2, "0");
      const month = String(messageDate.getMonth() + 1).padStart(2, "0");
      const year = messageDate.getFullYear();
      return `${day}/${month}/${year}`;
    } else {
      return formatIndianTime(timestamp);
    }
  };

  const markMessagesAsRead = async (chat) => {
    const chatId = chat.quotationId;
    const conversationKey = getConversationKey(chat);
    try {
      setChatList((prevList) =>
        prevList.map((chatItem) =>
          getConversationKey(chatItem) === conversationKey
            ? { ...chatItem, unreadCount: 0 }
            : chatItem
        )
      );

      await axios.post(
        `${BASE_API_CONFIG.baseURL}/messages/mark-as-read/${chatId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const handleChatClick = (chat) => {
    const updatedChat = {
      ...chat,
      chatPartnerId: chat.receiverId === userId ? chat.senderId : chat.receiverId,
    };

    if (chat.unreadCount > 0) {
      markMessagesAsRead(chat);
    }

    setSelectedChat(updatedChat);
    setSelectedChatRatings(chat);

    if (isMobile) {
      setShowChatDetails(true);
    }
  };

  const handleSendMessage = () => {
    setSendMessage("");
    fetchChatList();
  };

  const handleChatUpdate = (updatedChat) => {
    setSelectedChat(updatedChat);
  };

  const handleBackToChatList = () => {
    setShowChatDetails(false);
  };

  const logoFromString = (str) => {
    if (!isValidString(str)) return "";
    const words = str.trim().split(" ");
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    } else {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
  };

  const renderCompanyLogo = useCallback((logoUrl, companyName) => {
    return (
      <>
        {isValidString(logoUrl) ? (
          <Avatar
            src={logoUrl}
            className="gx-avatar gx-pointer"
            style={{
              height: 48,
              width: 48,
              border: "3px solid rgba(24, 144, 255, 0.1)",
              boxShadow: "0 4px 12px rgba(24, 144, 255, 0.15)",
            }}
          />
        ) : (
          <Avatar
            className="gx-avatar gx-pointer"
            style={{
              height: 48,
              width: 48,
              background: "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
              border: "3px solid rgba(24, 144, 255, 0.1)",
              fontWeight: "600",
              fontSize: "16px",
              boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
            }}
          >
            {logoFromString(companyName)}
          </Avatar>
        )}
      </>
    );
  }, []);

  const filteredChatList = chatList.filter((chat) => {
    const senderName = chat?.sender?.companyName.toLowerCase() || "";
    const receiverName = chat?.receiver?.companyName.toLowerCase() || "";
    const query = searchQuery.toLowerCase();

    return senderName.includes(query) || receiverName.includes(query);
  });

  return (
    <div
      style={{
        padding: "16px",
        background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
        minHeight: "100vh",
      }}
    >
      <style>
        {`
          .chat-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          
          .chat-scrollbar::-webkit-scrollbar-track {
            background: rgba(240, 240, 240, 0.1);
            border-radius: 10px;
          }
          
          .chat-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(24, 144, 255, 0.3);
            border-radius: 10px;
          }
          
          .chat-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(24, 144, 255, 0.5);
          }
          
          .chat-item-hover {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          .chat-item-hover:hover {
            transform: translateX(8px);
            box-shadow: 0 8px 25px rgba(24, 144, 255, 0.15) !important;
          }
          
          .search-input-focus {
            border-color: #0062FF !important;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
          }
        `}
      </style>

      <div
        style={{
          display: "flex",
          gap: "24px",
          height: "calc(100vh - 64px)",
          flexDirection: isMobile ? "column" : "row",
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Chat List Sidebar */}
        <div
          style={{
            flex: isMobile ? "0 0 auto" : "0 0 380px",
            background: "white",
            borderRadius: "24px",
            padding: "0",
            height: isMobile ? "auto" : "100%",
            display: isMobile && showChatDetails ? "none" : "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(24, 144, 255, 0.12)",
            border: "1px solid rgba(24, 144, 255, 0.08)",
            overflow: "hidden",
          }}
        >
          {/* Chat List Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #1890ff 0%, #1890ff 100%)",
              padding: "24px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Typography.Title
              level={4}
              style={{
                color: "white",
                margin: "0 0 20px 0",
                fontSize: "22px",
                fontWeight: "600",
                zIndex: 1,
                position: "relative",
              }}
            >
              Messages
            </Typography.Title>

            <Input
              placeholder="Search conversations..."
              style={{
                borderRadius: "20px",
                border: "2px solid rgba(255, 255, 255, 0.2)",
                background: "rgba(255, 255, 255, 0.1)",
                color: "white",
                fontSize: "14px",
                padding: "12px 16px",
                height: "auto",
                zIndex: 1,
                position: "relative",
              }}
              suffix={<SearchOutlined style={{ color: "rgba(255, 255, 255, 0.7)" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Chat Items */}
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "300px",
                gap: "16px",
              }}
            >
              <LoadingOutlined
                style={{ fontSize: 48, color: "#0062FF" }}
                spin
              />
              <Typography.Text
                style={{
                  color: "#666",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                Loading conversations...
              </Typography.Text>
            </div>
          ) : (
            <div
              className="chat-scrollbar"
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px 0",
              }}
            >
              <List
                dataSource={filteredChatList}
                renderItem={(chat) => (
                  <List.Item
                    key={getConversationKey(chat)}
                    onClick={() => handleChatClick(chat)}
                    className="chat-item-hover"
                    style={{
                      cursor: "pointer",
                      background:
                        getConversationKey(selectedChat) === getConversationKey(chat)
                          ? "linear-gradient(90deg, rgba(24, 144, 255, 0.08) 0%, rgba(24, 144, 255, 0.04) 100%)"
                          : "transparent",
                      borderRight:
                        getConversationKey(selectedChat) === getConversationKey(chat)
                          ? "4px solid #0062FF"
                          : "4px solid transparent",
                      margin: "4px 16px",
                      borderRadius: "16px",
                      padding: "16px 20px",
                      border: "1px solid rgba(24, 144, 255, 0.05)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "16px",
                        width: "100%",
                      }}
                    >
                      {renderCompanyLogo(
                        chat.receiverId === userId
                          ? chat.sender.companyLogo
                          : chat.receiver.companyLogo,
                        chat.receiverId === userId
                          ? chat.sender.companyName
                          : chat.receiver.companyName
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "8px",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography.Text
                              strong
                              style={{
                                fontSize: "15px",
                                color: "#1a1a1a",
                                fontWeight: "600",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {chat.receiverId === userId
                                ? chat.sender.companyName
                                : chat.receiver.companyName}
                            </Typography.Text>
                            <Typography.Text
                              style={{
                                fontSize: "12px",
                                color: "#0062FF",
                                fontWeight: "500",
                                background: "rgba(24, 144, 255, 0.1)",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                display: "inline-block",
                                marginTop: "4px",
                              }}
                            >
                              {chat.businessType}
                            </Typography.Text>
                          </div>

                          <Typography.Text
                            style={{
                              fontSize: "11px",
                              color: "#999",
                              fontWeight: "500",
                              marginLeft: "12px",
                              flexShrink: 0,
                            }}
                          >
                            {chat.latestMessageTimestamp
                              ? formatTimestamp(chat.latestMessageTimestamp)
                              : ""}
                          </Typography.Text>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography.Text
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            }}
                          >
                            {chat.lastMessageText.length > 35
                              ? chat.lastMessageText.substring(0, 35) + "..."
                              : chat.lastMessageText || "No messages yet"}
                          </Typography.Text>

                          {chat.unreadCount > 0 && (
                            <Badge
                              count={chat.unreadCount}
                              style={{
                                background:
                                  "linear-gradient(135deg, #1890ff 0%,#0062FF 100%)",
                                fontSize: "11px",
                                fontWeight: "600",
                                boxShadow: "0 2px 8px rgba(24, 144, 255, 0.4)",
                                marginLeft: "12px",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          )}
        </div>

        {/* Chat Details */}
        {selectedChat && (
          <div
            style={{
              flex: isMobile ? "1 1 auto" : "1 1 auto",
              display: isMobile && !showChatDetails ? "none" : "block",
              minHeight: isMobile ? "600px" : "auto",
            }}
          >
            <ChatDetails
              selectedChat={selectedChat}
              sendMessage={sendMessage}
              setSendMessage={setSendMessage}
              handleSendMessage={handleSendMessage}
              onMessageSent={fetchChatList}
              onChatUpdate={handleChatUpdate}
              onBack={isMobile ? handleBackToChatList : undefined}
            />
          </div>
        )}

        {/* Empty State */}
        {!selectedChat && !isMobile && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "white",
              borderRadius: "24px",
              padding: "48px",
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(24, 144, 255, 0.12)",
              border: "1px solid rgba(24, 144, 255, 0.08)",
            }}
          >
            <div
              style={{
                width: "120px",
                height: "120px",
                background: "linear-gradient(135deg, #1890ff 0%, #0062FF 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
                boxShadow: "0 8px 32px rgba(24, 144, 255, 0.3)",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                  fill="white"
                />
              </svg>
            </div>
            <Typography.Title
              level={3}
              style={{
                color: "#0062FF",
                marginBottom: "12px",
                fontWeight: "600",
              }}
            >
              Select a conversation
            </Typography.Title>
            <Typography.Text
              style={{
                color: "#666",
                fontSize: "16px",
                lineHeight: "1.6",
                maxWidth: "300px",
              }}
            >
              Choose a conversation from the sidebar to start messaging.
            </Typography.Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
